import clsx from "clsx";
import { Check, Loader2 } from "lucide-react";
import {
  useId,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "danger-solid";

const VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-accent text-accent-fg hover:bg-accent-strong",
  secondary: "border border-line bg-surface text-text hover:bg-surface-2",
  ghost: "text-muted hover:bg-surface-2 hover:text-text",
  danger: "border border-line bg-surface text-danger hover:bg-danger-soft",
  "danger-solid": "bg-danger text-white hover:opacity-90",
};

export function Button({
  variant = "primary",
  busy = false,
  icon,
  className,
  children,
  disabled,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  busy?: boolean;
  icon?: ReactNode;
}) {
  return (
    <button
      type="button"
      {...rest}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={clsx(
        "inline-flex min-h-10 items-center justify-center gap-2 rounded-lg px-4 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60",
        VARIANTS[variant],
        className,
      )}
    >
      {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  );
}

/** Multi-line field (public keys, cloud-init); same label, hint and error layout as `TextField`. */
export function TextArea({
  label,
  hint,
  error,
  info,
  className,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  info?: ReactNode;
}) {
  const id = useId();
  const describedBy = [hint && `${id}-hint`, error && `${id}-error`].filter(Boolean).join(" ");
  return (
    <div className={clsx("flex flex-col gap-1.5", className)}>
      <span className="flex items-center gap-1.5">
        <label htmlFor={id} className="text-sm font-medium">
          {label}
        </label>
        {info}
      </span>
      <textarea
        id={id}
        {...rest}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        className={clsx(
          "min-h-24 rounded-lg border bg-surface px-3 py-2 font-mono text-xs text-text placeholder:text-muted",
          error ? "border-danger" : "border-line focus:border-accent",
        )}
      />
      {hint && (
        <p id={`${id}-hint`} className="text-xs text-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

export function TextField({
  label,
  hint,
  error,
  info,
  icon,
  hideLabel = false,
  compact = false,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  /** Element after the label, usually a `FieldHint`. */
  info?: ReactNode;
  /** A small icon inside the field, on the left (search fields). */
  icon?: ReactNode;
  /** The label is for screen readers only (a toolbar's search, where the placeholder says it). */
  hideLabel?: boolean;
  /** Toolbar height (32 px) instead of the form height. */
  compact?: boolean;
}) {
  const id = useId();
  const describedBy = [hint && `${id}-hint`, error && `${id}-error`].filter(Boolean).join(" ");
  return (
    <div className={clsx("flex flex-col gap-1.5", className)}>
      <span className={clsx("flex items-center gap-1.5", hideLabel && "sr-only")}>
        <label htmlFor={id} className="text-sm font-medium">
          {label}
        </label>
        {info}
      </span>
      <span className="relative flex">
        {icon && (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted">
            {icon}
          </span>
        )}
        <input
          id={id}
          {...rest}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
          className={clsx(
            "w-full min-w-0 rounded-lg border bg-surface text-sm text-text placeholder:text-muted",
            compact ? "min-h-8 px-2" : "min-h-10 px-3",
            icon && "pl-9",
            error ? "border-danger" : "border-line focus:border-accent",
          )}
        />
      </span>
      {hint && (
        <p id={`${id}-hint`} className="text-xs text-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * One choice of a list: a native radio (keyboard, screen readers) with our
 * look, a check when chosen. The one radio style of the panel (terminal
 * settings, the connect dialog).
 */
export function Choice({
  name,
  checked,
  onChange,
  children,
  hint,
  icon,
  style,
}: {
  name: string;
  checked: boolean;
  onChange: () => void;
  children: ReactNode;
  hint?: string | undefined;
  /** Before the text (the kind of a session). */
  icon?: ReactNode;
  style?: CSSProperties | undefined;
}) {
  return (
    <label
      className={clsx(
        "flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border px-3 py-1.5 text-sm",
        "has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-accent",
        checked ? "border-accent bg-accent-soft" : "border-line hover:bg-surface-2",
      )}
    >
      <input type="radio" name={name} className="sr-only" checked={checked} onChange={onChange} />
      {icon && <span className="shrink-0 text-muted">{icon}</span>}
      <span className="flex min-w-0 flex-1 flex-col">
        <span
          className="truncate"
          style={style}
          title={typeof children === "string" ? children : undefined}
        >
          {children}
        </span>
        {hint && <span className="text-xs text-muted">{hint}</span>}
      </span>
      <Check
        className={clsx("size-4 shrink-0 text-accent-strong", !checked && "invisible")}
        aria-hidden
      />
    </label>
  );
}

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <section className={clsx("rounded-xl border border-line bg-surface p-5 shadow-sm", className)}>
      {children}
    </section>
  );
}

type Tone = "neutral" | "ok" | "warn" | "danger";
const TONES: Record<Tone, string> = {
  neutral: "bg-surface-2 text-muted",
  ok: "bg-ok-soft text-ok",
  warn: "bg-warn-soft text-warn",
  danger: "bg-danger-soft text-danger",
};

/**
 * A short status label. `wrap` lets a longer one («Предупреждения и критичные») break
 * across lines where a narrow card has no room for it on one.
 */
export function Badge({
  tone = "neutral",
  wrap = false,
  children,
}: {
  tone?: Tone;
  wrap?: boolean;
  children: ReactNode;
}) {
  return (
    <span
      className={clsx(
        "inline-flex items-center px-2 py-0.5 text-xs font-medium",
        wrap ? "max-w-full rounded-lg" : "whitespace-nowrap rounded-full",
        TONES[tone],
      )}
    >
      {children}
    </span>
  );
}

export function Alert({ tone = "warn", children }: { tone?: Tone; children: ReactNode }) {
  return (
    <div
      role={tone === "danger" ? "alert" : "status"}
      className={clsx("rounded-lg px-4 py-3 text-sm", TONES[tone])}
    >
      {children}
    </div>
  );
}

export function Spinner({ label }: { label: string }) {
  return (
    <div role="status" className="flex items-center gap-2 p-6 text-sm text-muted">
      <Loader2 className="size-4 animate-spin" aria-hidden />
      {label}
    </div>
  );
}

/** Compact native select for filters and toolbars; native keeps the phone's own picker. */
export function Select({
  label,
  className,
  children,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { label: string }) {
  return (
    <select
      aria-label={label}
      {...rest}
      className={clsx(
        "min-h-9 rounded-lg border border-line bg-surface px-3 text-sm text-text focus:border-accent",
        className,
      )}
    >
      {children}
    </select>
  );
}
